import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { toast } from "sonner";
import { SectionCard } from "./shared";
import { NEW_LEDGER_OPTION, vendorBlank } from "./constants";
import { ACCOUNTING_BASE } from "./accountingNav";
import VendorForm from "./VendorForm";

export default function VendorEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [vendorLedgers, setVendorLedgers] = useState([]);
  const [form, setForm] = useState(vendorBlank);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [vendorRes, ledgersRes] = await Promise.all([
          api.get(`/society/accounting/engine/vendors/${id}`),
          api.get("/society/accounting/engine/ledgers").catch(() => ({ data: [] })),
        ]);
        const row = vendorRes.data;
        setVendor(row);
        setVendorLedgers((Array.isArray(ledgersRes.data) ? ledgersRes.data : []).filter((l) => l.ledgerGroup === "vendor"));
        setForm({
          vendorName: row.vendorName || "", contactPerson: row.contactPerson || "", mobile: row.mobile || "", email: row.email || "",
          address: row.address || "", gstin: row.gstin || "", pan: row.pan || "", paymentTerms: row.paymentTerms || "",
          linkedLedgerId: row.linkedLedgerId || NEW_LEDGER_OPTION, newLedgerName: "", active: row.active !== false, notes: row.notes || "",
          // Prefilled from the linked ledger's current opening balance (Phase
          // 10 Tally-style redesign) - see BankAccountEditPage for why.
          openingBalance: row.openingBalance || 0, openingBalanceSide: row.openingBalanceSide || "credit",
          openingAsOfDate: row.openingAsOfDate || "",
        });
      } catch (err) {
        toast.error(formatErr(err, "Could not load this vendor"));
        navigate(`${ACCOUNTING_BASE}/vendors`, { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const save = async () => {
    if (!form.vendorName.trim()) { toast.error("Vendor name is required"); return; }
    try {
      setBusy(true);
      let linkedLedgerId = form.linkedLedgerId;
      const openingBalance = form.openingBalance === "" ? 0 : Number(form.openingBalance);
      if (linkedLedgerId === NEW_LEDGER_OPTION) {
        const ledgerName = (form.newLedgerName || `${form.vendorName} payable`).trim();
        const ledgerRes = await api.post("/society/accounting/engine/ledgers", {
          name: ledgerName, ledgerGroup: "vendor", openingBalance, openingSide: form.openingBalanceSide,
          openingAsOfDate: form.openingAsOfDate,
        });
        linkedLedgerId = ledgerRes.data.id;
      }
      await api.patch(`/society/accounting/engine/vendors/${id}`, { ...form, linkedLedgerId, openingBalance });
      toast.success("Vendor updated");
      navigate(`${ACCOUNTING_BASE}/vendors/${id}`);
    } catch (err) {
      toast.error(formatErr(err, "Could not save vendor"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-sm text-[#6B7280]">Loading...</div>;
  if (!vendor) return null;

  return (
    <div className="space-y-5">
      <div>
        <Link to={`${ACCOUNTING_BASE}/vendors/${id}`} className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6B7280] hover:text-[#111827]">
          &larr; Back to vendor
        </Link>
        <h2 className="mt-1 text-lg font-extrabold text-[#111827]">Edit {vendor.vendorName}</h2>
      </div>

      <SectionCard title="Vendor details">
        <VendorForm
          form={form}
          setForm={setForm}
          availableLedgers={vendorLedgers}
          ledgerLocked={!!vendor.hasPostedTransactions}
          busy={busy}
          onSubmit={save}
          onCancel={() => navigate(`${ACCOUNTING_BASE}/vendors/${id}`)}
          submitLabel="Save changes"
          isEdit
        />
      </SectionCard>
    </div>
  );
}
